import * as React from 'react';
import classnames from 'classnames';

import { Venues } from '@weddingspot/ws-api-client';

import StyleIcon from '../icon-style';
import ServicesIcon from '../icon-services';
import GoogleMapEmbed from '../google-map-embed';
import { ServiceProps } from '../service-options-modal/Services';

const styles = require('./VenueDetails.scss');

export interface IProps {
    name: string;
    description: string;
    awards: Venues.VenueAward[];
    styles: {
        id: number;
        name: string;
    }[];
    ceremonies: ServiceProps[];
    receptions: ServiceProps[];
    rds: ServiceProps[];
    capacityMax?: number;
    capacityMin?: number;

    // Address
    address: {
        address1?: string;
        address2?: string;
        city: string;
        zip_code: string | null;
        state: string;
        country: string;
        province: string | null;
    };

    // For the map
    gps?: {
        ltd: number;
        lng: number;
    };

    // Event handler for
    handleServiceOptionsClick: () => void;
}

interface IState {
    mWebDescriptionExpanded: boolean;
}

export default class VenueDetails extends React.Component<IProps, IState> {
    readonly state: IState = {
        mWebDescriptionExpanded: false,
    };

    constructor(props: IProps) {
        super(props);
        this.expandDescription = this.expandDescription.bind(this);

        /*'C-' is used in database records for differentiating CI states from others. Therefore, correcting state codes for UI.
        for example: Puerto Rico is PR for United States and C-PR for Caribbean Islands.*/
        this.props.address.state = this.props.address.state.replace('C-', '');
    }

    private renderAddress() {
        /*
            Valid address should have on of the
            following representations:

            City, Province 12345
            City, Province
            City, 12345
            Province 12345
            City
            Province
            12345
            ''
        */
        const { city, state, province, zip_code, country } = this.props.address;

        let tokens = [];
        
        if (!!city) {
            tokens.push(city);
        }
        if (!!state) {
            tokens.push(state);
        } else if (!!province) {
            tokens.push(province);
        }

        let cityLine = tokens.join(', ');
        if (!!zip_code) {
            if (!!cityLine) {
                cityLine += ` ${zip_code}`;
            } else {
                cityLine = `${zip_code}`;
            }
        }
        return (
            <span>
                {cityLine}
                {!state && !!cityLine && <br />}
                {!state && country}
            </span>
        );
    }

    public render() {
        const { description, capacityMax, capacityMin, ceremonies, receptions, rds, address, handleServiceOptionsClick } = this.props;

        // Process service strings
        let serviceCats = [];
        let rdService = null;
        if (ceremonies.length > 0) {
            serviceCats.push('Ceremony');
        }
        if (receptions.length > 0) {
            serviceCats.push('Reception');
        }
        if (rds.length > 0) {
            rdService = 'Rehearsal Dinner / Bridal Shower';
        }

        const serviceCatStr = serviceCats.join(' / ');

        const indoors =
            !!ceremonies.filter((item) => item.type === 'indoor').length || !!receptions.filter((item) => item.type === 'indoor').length;
        const outdoors =
            !!ceremonies.filter((item) => item.type === 'outdoor').length || !!receptions.filter((item) => item.type === 'outdoor').length;
        let serviceTypes = [];
        if (indoors) {
            serviceTypes.push('Indoor');
        }
        if (outdoors) {
            serviceTypes.push('Outdoor');
        }
        const serviceTypesStr = serviceTypes.join(' / ');

        // Capacity string
        let capacityStr = '';
        if (!!capacityMax || !!capacityMin) {
            if (!!capacityMax) {
                capacityStr = `Accommodates up to ${capacityMax} guests`;
            } else {
                capacityStr = `Accommodates at least ${capacityMin}`;
            }
        }

        return (
            <div className='VenuePage--details-wrapper VenuePage--main-details-wrapper'>
                <div className='VenuePage--main-details'>
                    <div className='VenuePage--detail'>
                        <span className='VenuePage--detail-icon'>
                            <StyleIcon />
                        </span>
                        <div className='VenuePage--detail-text-container'>
                            <h3 className='VenuePage--detail-headline'>Style:</h3>
                            <p className='VenuePage--detail-description'>{this.props.styles.map((s) => s.name).join(', ')}</p>
                        </div>
                    </div>
                    {!!capacityStr && (
                        <div className='VenuePage--detail'>
                            <span className='VenuePage--detail-icon icon-hb-nx-users' />
                            <div className='VenuePage--detail-text-container'>
                                <h3 className='VenuePage--detail-headline'>Guest capacity:</h3>
                                <p className='VenuePage--detail-description'>{capacityStr}</p>
                            </div>
                        </div>
                    )}

                    <div className='VenuePage--detail'>
                        <span className='VenuePage--detail-icon'>
                            <ServicesIcon />
                        </span>
                        <div className='VenuePage--detail-text-container'>
                            <h3 className='VenuePage--detail-headline'>Services:</h3>
                            <p className='VenuePage--detail-description'>
                                {serviceCatStr}
                                {serviceCatStr.length > 0 && rdService && [' / ', <br key={1} />]}
                                {rdService}
                            </p>
                            <p className='VenuePage--detail-description'>{serviceTypesStr}</p>
                            <div
                                className={classnames('VenuePage--detail-link', 'VenuePage--detail-description', styles.visibleMobile)}
                                onClick={handleServiceOptionsClick}
                            >
                                VIEW OPTIONS
                            </div>
                        </div>
                    </div>
                    <div className='VenuePage--detail'>
                        <span className='VenuePage--detail-icon icon-hb-nx-location-pin-32' />
                        <div className='VenuePage--detail-text-container'>
                            <h3 className='VenuePage--detail-headline'>Location:</h3>

                            <p className='VenuePage--detail-description'>
                                {address.address1 || 'Private Estate'} <br />
                                {!!address.address2 && [<span key={0}>{address.address2}</span>, <br key={1} />]}
                                {this.renderAddress()}
                            </p>
                        </div>
                    </div>

                    {/* Currently international addresses dont have gps coords, so just hiding the map. */}
                    {address.state !== null && (
                        <div className='VenuePage--map-wrapper'>
                            <GoogleMapEmbed width={300} height={230} name={this.props.name} address={address} />
                        </div>
                    )}
                </div>

                <div className='VenuePage--description-details'>
                    {this.props.awards.length > 0 && (
                        <div className='VenuePage--detail'>
                            <h3 className='VenuePage--detail-headline VenuePage--detail-headline__award'>Awards:</h3>
                            {this.props.awards.map((award, index) => this.renderAward(award, index))}
                        </div>
                    )}

                    <div className='VenuePage--detail VenuePage--detail__description'>
                        <h3 className='VenuePage--detail-headline VenuePage--detail-headline__description'>Description:</h3>
                        <div
                            className={classnames('VenuePage--description', {
                                'VenuePage--description__expanded': this.state.mWebDescriptionExpanded,
                            })}
                        >
                            {description.split('\r\n').map((chunk, idx) => (
                                <p key={idx}>{chunk || '\u00A0'} </p>
                            ))}
                        </div>
                        {!this.state.mWebDescriptionExpanded && (
                            <div className={classnames(styles.taCenter, styles.visibleMobile)}>
                                <a onClick={this.expandDescription} className='VenuePage--read-more-button'>
                                    READ MORE <i className='icon-hb-nx-arrow-down-16' />
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private expandDescription() {
        this.setState({ mWebDescriptionExpanded: true });
    }

    private renderAward(award: Venues.VenueAward, index: number) {
        return (
            <a key={index} href={award.page_url} className='VenuePage--award-badge-link'>
                <img src={award.image_url} alt={award.description} className='VenuePage--award-img' />
            </a>
        );
    }
}
